import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from "@mui/material";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

const EmployeeFormDialog = ({
  open,
  onClose,
  onSave,
  employee = null,
  locations = [],
  jobPositions = [],
  employees = [],
  loading = false,
}) => {
  const isEdit = Boolean(employee);
  const [form, setForm] = useState({});
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          employeeId: employee.employeeId,
          firstName: employee.firstName || "",
          lastName: employee.lastName || "",
          mail: employee.mail || "",
          businessEmail: employee.businessEmail || "",
          jobPositionId: employee.jobPositionId || "",
          managerId: employee.managerId || "",
          admission: employee.admission
            ? employee.admission.split("T")[0]
            : "",
          officePhone: employee.officePhone || "",
          extension: employee.extension || "",
          phone2: employee.phone2 || "",
          gender: employee.gender || "",
          dob: employee.dob ? employee.dob.split("T")[0] : "",
        });
        setSelectedLocationId("");
      } else {
        setForm({
          firstName: "",
          lastName: "",
          mail: "",
          businessEmail: "",
          jobPositionId: "",
          managerId: "",
          admission: new Date().toISOString().split("T")[0],
          officePhone: "",
          extension: "",
          phone2: "",
          gender: "",
          dob: "",
        });
        setSelectedLocationId("");
      }
      setError("");
    }
  }, [open, employee, isEdit]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = () => {
    if (!form.firstName?.trim()) return setError("First name is required");
    if (!form.lastName?.trim()) return setError("Last name is required");
    if (!form.jobPositionId) return setError("Job position is required");
    if (!form.admission) return setError("Admission date is required");
    if (!isEdit && !selectedLocationId)
      return setError("At least one location is required");

    const data = { ...form };
    if (!isEdit) {
      data.locations = JSON.stringify([
        { NUM_ID_LOCATION: Number(selectedLocationId), BIT_PRIMARY: 1 },
      ]);
    }
    Object.keys(data).forEach((k) => {
      if (data[k] === "") data[k] = isEdit ? null : undefined;
    });
    if (data.jobPositionId) data.jobPositionId = Number(data.jobPositionId);
    if (data.managerId) data.managerId = Number(data.managerId);

    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Employee" : "New Employee"}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="First Name *"
              value={form.firstName || ""}
              onChange={handleChange("firstName")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Last Name *"
              value={form.lastName || ""}
              onChange={handleChange("lastName")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Personal Email"
              value={form.mail || ""}
              onChange={handleChange("mail")}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Business Email"
              value={form.businessEmail || ""}
              onChange={handleChange("businessEmail")}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Job Position *</InputLabel>
              <Select
                value={form.jobPositionId || ""}
                label="Job Position *"
                onChange={handleChange("jobPositionId")}
              >
                {jobPositions.map((jp) => (
                  <MenuItem key={jp.jobPositionId} value={jp.jobPositionId}>
                    {jp.jobPositionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Manager</InputLabel>
              <Select
                value={form.managerId || ""}
                label="Manager"
                onChange={handleChange("managerId")}
              >
                <MenuItem value="">None</MenuItem>
                {employees
                  .filter((e) => e.status === 1)
                  .map((e) => (
                    <MenuItem key={e.employeeId} value={e.employeeId}>
                      {e.firstName} {e.lastName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Admission Date *"
              value={form.admission || ""}
              onChange={handleChange("admission")}
              fullWidth
              size="small"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Date of Birth"
              value={form.dob || ""}
              onChange={handleChange("dob")}
              fullWidth
              size="small"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Office Phone"
              value={form.officePhone || ""}
              onChange={handleChange("officePhone")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Extension"
              value={form.extension || ""}
              onChange={handleChange("extension")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Phone 2"
              value={form.phone2 || ""}
              onChange={handleChange("phone2")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={form.gender || ""}
                label="Gender"
                onChange={handleChange("gender")}
              >
                <MenuItem value="">—</MenuItem>
                {GENDERS.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {!isEdit && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Primary Location *</InputLabel>
                <Select
                  value={selectedLocationId}
                  label="Primary Location *"
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                >
                  {locations
                    .filter((l) => l.status === 1)
                    .map((l) => (
                      <MenuItem key={l.locationId} value={l.locationId}>
                        {l.medicalCenter}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeFormDialog;
